/**
 * Extracted Totals card: Subtotal, Freight, Fuel, Taxes, Discount, Total (from image).
 * Restocking is modeled only on the Restocking Fee tab (not duplicated here).
 * Flags and revert icons for reconciliation issues. Kept under 200 lines.
 */
import React from 'react';
import { Box, Flex, HStack, Icon, IconButton, Text, Tooltip, VStack } from '@chakra-ui/react';
import { TbArrowBackUp, TbFlag } from 'react-icons/tb';
import type { InvoiceDetailResponse } from '../../types/invoice';
import type { ZoomImageHighlightBox } from '../ZoomImage';

const ACCENT = '#0071E3';

export interface InvoiceTotalsCardProps {
    invoice: InvoiceDetailResponse;
    getNumberValue: (field: keyof InvoiceDetailResponse) => number;
    isFieldEdited: (field: keyof InvoiceDetailResponse) => boolean;
    /** Used when editable; in read-only mode we render plain text instead. */
    renderNumericField: (fieldName: keyof InvoiceDetailResponse, label?: string, fontWeight?: string, valueColor?: string) => React.ReactNode;
    openRevertModal: (fieldKey: string, fieldLabel: string) => void;
    fmtUsd: (n: number) => string;
    /** When true, show extracted totals as plain text (no editing affordances). */
    isReadOnly: boolean;
    /** Live mismatches from current form values (before save and after—same as persisted when unchanged). */
    localGrandTotalVsAboveLineItemsWithFeesMismatch?: {
        extractedTotal: number;
        expectedFromAboveLineItems: number;
    } | null;
    /** Org invoicing setting: when true, freight is represented as a line item/tab and hidden here. */
    freightAsLineItem: boolean;
    /** When > 0, header fuel in this card is $0 (fuel is on the Fuel tab only). */
    fuelLineItemCount: number;
    /** Discount is sourced from extracted totals; display computed signed value here. */
    discountTotal: number;
    /** Credited / return / return-slip: +$ discount, −$ taxes; received: −$ discount, +$ taxes; leading − on credited order total. */
    creditedTotalsLayout?: boolean;
    /** When true, omit the section heading (parent provides foldable title). */
    hideTitle?: boolean;
    /** When true, hide taxes/discount rows (rendered by parent elsewhere). */
    hideAdditionalFees?: boolean;
    /** When true, render only the Total (from image) row. */
    totalOnly?: boolean;
    onFieldImageHighlightChange?: (region: ZoomImageHighlightBox | null) => void;
}

export function InvoiceTotalsCard(props: InvoiceTotalsCardProps) {
    const {
        invoice,
        getNumberValue,
        isFieldEdited,
        renderNumericField,
        openRevertModal,
        fmtUsd,
        isReadOnly,
        localGrandTotalVsAboveLineItemsWithFeesMismatch,
        freightAsLineItem,
        fuelLineItemCount,
        discountTotal,
        creditedTotalsLayout = false,
        hideTitle = false,
        hideAdditionalFees = false,
        totalOnly = false,
        onFieldImageHighlightChange,
    } = props;
    const grandVsAboveLineItemsMismatch = localGrandTotalVsAboveLineItemsWithFeesMismatch ?? null;
    const totalMismatch = !!grandVsAboveLineItemsMismatch;
    const inputWrap = (borderColor: string, bg: string) => ({
        w: '120px',
        border: '1px solid',
        borderColor,
        bg,
        borderRadius: 'md',
        _hover: { borderColor: 'gray.400' },
        _focusWithin: { borderColor: ACCENT, bg: 'blue.50' },
        px: 2,
        spacing: 0,
    });

    const taxes = getNumberValue('taxesTotal');
    const taxesMagnitude = Math.abs(taxes);
    const freightHeader = getNumberValue('freightTotal');
    const fuelHeader = getNumberValue('fuelTotal');
    const discountMagnitude = Math.abs(discountTotal ?? 0);
    const orderTotalValue = getNumberValue('orderTotal');
    const orderTotalMagnitude = Math.abs(orderTotalValue);

    /** Match fuel row: treat tiny floats as zero so 0.00 never gets debit/credit tint. */
    const nearlyZero = (n: number) => Math.abs(n) < 0.0001;
    const taxesNeutral = nearlyZero(taxesMagnitude);
    const discountNeutral = nearlyZero(discountMagnitude);
    const orderTotalNeutral = nearlyZero(orderTotalMagnitude);

    const discountSigned = getNumberValue('discountTotal');
    const SIGN_EPS = 0.0001;
    /** Credited tax row is shown as a deduction (−); received uses raw sign. */
    const taxesAmountNegative = !taxesNeutral && (creditedTotalsLayout || taxes < -SIGN_EPS);
    const discountAmountNegative = !discountNeutral && discountSigned < -SIGN_EPS;
    const orderAmountNegative = !orderTotalNeutral && orderTotalValue < -SIGN_EPS;
    const taxesTone = taxesAmountNegative ? 'red.500' : 'black';
    const discountTone = discountAmountNegative ? 'red.500' : 'black';
    const orderTone = orderAmountNegative ? 'red.500' : 'black';

    const isFreightAsLineItem = freightAsLineItem;
    const isFuelAsLineItem = fuelLineItemCount > 0;
    /** Header freight/fuel only — never duplicate tab line totals in this section. */
    const freightDisplay = isFreightAsLineItem ? 0 : freightHeader;
    const fuelDisplay = isFuelAsLineItem ? 0 : fuelHeader;

    return (
        <Box>
            {!hideTitle && (
                <Text color="gray.900" fontSize="sm" fontWeight="bold" mb={3}>
                    Extracted Totals
                </Text>
            )}
            <VStack spacing={0} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden" p={5}>
                <VStack spacing={2} align="stretch" w="100%">
                    {!totalOnly && !isFreightAsLineItem && (
                        <Flex justify="space-between" align="center">
                            <Text fontSize="sm" color="gray.600">Freight</Text>
                            <HStack spacing={1}>
                                <HStack {...inputWrap('gray.200', 'transparent')}>
                                    <Text fontSize="md" color="black" flexShrink={0}>$</Text>
                                    <Box flex="1" textAlign="right">
                                        {isReadOnly || isFreightAsLineItem ? fmtUsd(freightDisplay) : renderNumericField('freightTotal')}
                                    </Box>
                                </HStack>
                                {!isReadOnly && !isFreightAsLineItem && isFieldEdited('freightTotal') && (
                                    <Tooltip label="Value changed" placement="top" hasArrow>
                                        <IconButton aria-label="Compare" icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />} size="xs" variant="ghost" onClick={() => openRevertModal('freightTotal', 'Freight')} _hover={{ bg: 'gray.100' }} minW="auto" h="auto" p={0} />
                                    </Tooltip>
                                )}
                            </HStack>
                        </Flex>
                    )}

                    {!totalOnly && (Math.abs(fuelDisplay) > 0.0001 || Math.abs(invoice?.fuelTotal ?? 0) > 0.0001) && (
                        <Flex justify="space-between" align="center">
                            <Text fontSize="sm" color="gray.600">Fuel</Text>
                            <HStack spacing={1}>
                                <HStack {...inputWrap('gray.200', 'transparent')}>
                                    <Text fontSize="md" color="black" flexShrink={0}>$</Text>
                                    <Box flex="1" textAlign="right">
                                        {isReadOnly || isFuelAsLineItem ? fmtUsd(fuelDisplay) : renderNumericField('fuelTotal')}
                                    </Box>
                                </HStack>
                                {!isReadOnly && !isFuelAsLineItem && isFieldEdited('fuelTotal') && (
                                    <Tooltip label="Value changed" placement="top" hasArrow>
                                        <IconButton aria-label="Compare" icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />} size="xs" variant="ghost" onClick={() => openRevertModal('fuelTotal', 'Fuel')} _hover={{ bg: 'gray.100' }} minW="auto" h="auto" p={0} />
                                    </Tooltip>
                                )}
                            </HStack>
                        </Flex>
                    )}

                    {!totalOnly && !hideAdditionalFees && (
                        <>
                            <Flex
                                justify="space-between"
                                align="center"
                                onMouseEnter={() => onFieldImageHighlightChange?.(invoice.imageFieldHighlights?.discountTotal ?? null)}
                                onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                            >
                                <Text fontSize="sm" color="gray.600">Discount</Text>
                                <HStack spacing={1}>
                                    <HStack {...inputWrap('gray.200', 'transparent')}>
                                        {!discountNeutral && (
                                            <>
                                                <Text fontSize="md" color={discountTone} flexShrink={0} userSelect="none">
                                                    {creditedTotalsLayout ? '+' : '-'}
                                                </Text>
                                                <Text fontSize="md" color={discountTone} flexShrink={0}>$</Text>
                                            </>
                                        )}
                                        {discountNeutral && (
                                            <Text fontSize="md" color={discountTone} flexShrink={0}>$</Text>
                                        )}
                                        <Box flex="1" textAlign="right" fontWeight="medium">
                                            {isReadOnly ? (
                                                <Text as="span" color={discountTone}>
                                                    {fmtUsd(discountMagnitude)}
                                                </Text>
                                            ) : (
                                                renderNumericField('discountTotal', undefined, 'medium', discountTone)
                                            )}
                                        </Box>
                                    </HStack>
                                    {!isReadOnly && isFieldEdited('discountTotal') && (
                                        <Tooltip label="Value changed" placement="top" hasArrow>
                                            <IconButton aria-label="Compare" icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />} size="xs" variant="ghost" onClick={() => openRevertModal('discountTotal', 'Discount')} _hover={{ bg: 'gray.100' }} minW="auto" h="auto" p={0} />
                                        </Tooltip>
                                    )}
                                </HStack>
                            </Flex>

                            <Flex
                                justify="space-between"
                                align="center"
                                onMouseEnter={() => onFieldImageHighlightChange?.(invoice.imageFieldHighlights?.taxesTotal ?? null)}
                                onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                            >
                                <Text fontSize="sm" color="gray.600">Taxes</Text>
                                <HStack spacing={1}>
                                    <HStack {...inputWrap('gray.200', 'transparent')}>
                                        {creditedTotalsLayout && !taxesNeutral && (
                                            <Text fontSize="md" color={taxesTone} flexShrink={0} userSelect="none">-</Text>
                                        )}
                                        {!creditedTotalsLayout && !taxesNeutral && (
                                            <Text fontSize="md" color={taxesTone} flexShrink={0} userSelect="none">+</Text>
                                        )}
                                        <Text fontSize="md" color={taxesTone} flexShrink={0}>$</Text>
                                        <Box flex="1" textAlign="right">
                                            {isReadOnly ? (
                                                <Text as="span" color={taxesTone}>
                                                    {fmtUsd(creditedTotalsLayout ? taxesMagnitude : taxes)}
                                                </Text>
                                            ) : (
                                                renderNumericField('taxesTotal', undefined, undefined, taxesTone)
                                            )}
                                        </Box>
                                    </HStack>
                                    {!isReadOnly && isFieldEdited('taxesTotal') && (
                                        <Tooltip label="Value changed" placement="top" hasArrow>
                                            <IconButton aria-label="Compare" icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />} size="xs" variant="ghost" onClick={() => openRevertModal('taxesTotal', 'Taxes')} _hover={{ bg: 'gray.100' }} minW="auto" h="auto" p={0} />
                                        </Tooltip>
                                    )}
                                </HStack>
                            </Flex>
                        </>
                    )}
                    {!totalOnly && <Box borderTop="1px solid" borderColor="gray.200" my={2} />}
                    <Flex
                        justify="space-between"
                        align="center"
                        onMouseEnter={() => onFieldImageHighlightChange?.(invoice.imageFieldHighlights?.orderTotal ?? null)}
                        onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                    >
                        <Text fontSize="lg" color="gray.900" fontWeight="bold">Total (from image)</Text>
                        <HStack spacing={1}>
                            {totalMismatch && (
                                <Tooltip
                                    label={
                                        <VStack align="start" spacing={1}>
                                            <Text fontSize="sm" fontWeight="semibold">
                                                Total doesn’t match expected amount (
                                                subtotal + extracted taxes + extracted discount
                                                {!isFreightAsLineItem ? ' + freight' : ''}
                                                )
                                            </Text>
                                            {grandVsAboveLineItemsMismatch && (
                                                <>
                                                    
                                                    <Text fontSize="xs">
                                                        Total: ${fmtUsd(grandVsAboveLineItemsMismatch.extractedTotal)} | Expected: $
                                                        {fmtUsd(grandVsAboveLineItemsMismatch.expectedFromAboveLineItems)}
                                                    </Text>
                                                </>
                                            )}
                                        </VStack>
                                    }
                                    placement="top"
                                    hasArrow
                                >
                                    <IconButton aria-label="Total formula mismatch" icon={<Icon as={TbFlag as React.ElementType} boxSize={4} />} variant="ghost" size="xs" color="orange.500" _hover={{ bg: 'transparent' }} cursor="pointer" minW="auto" h="auto" p={0} flexShrink={0} />
                                </Tooltip>
                            )}
                            {!isReadOnly && isFieldEdited('orderTotal') && (
                                <Tooltip label="Value changed" placement="top" hasArrow>
                                    <IconButton aria-label="Compare" icon={<Icon as={TbArrowBackUp as React.ElementType} w={4} h={4} color="orange.500" />} size="xs" variant="ghost" onClick={() => openRevertModal('orderTotal', 'Order total')} _hover={{ bg: 'gray.100' }} minW="auto" h="auto" p={0} flexShrink={0} />
                                </Tooltip>
                            )}
                            <HStack {...(totalMismatch ? inputWrap('orange.300', 'orange.50') : inputWrap('gray.200', 'transparent'))}>
                                {creditedTotalsLayout && !orderTotalNeutral && (
                                    <Text fontSize="md" color={orderTone} flexShrink={0} userSelect="none">
                                        -
                                    </Text>
                                )}
                                <Text fontSize="lg" color={orderTone} flexShrink={0}>$</Text>
                                <Box flex="1" textAlign="right" fontWeight="bold" fontSize="lg">
                                    {isReadOnly ? (
                                        <Text as="span" color={orderTone}>
                                            {fmtUsd(creditedTotalsLayout ? orderTotalMagnitude : orderTotalValue)}
                                        </Text>
                                    ) : (
                                        renderNumericField('orderTotal', undefined, 'bold', orderTone)
                                    )}
                                </Box>
                            </HStack>
                        </HStack>
                    </Flex>
                </VStack>
            </VStack>
        </Box>
    );
}
